import React, { createContext, useState, useEffect, useCallback, useRef, useContext } from 'react';
import { getBaseUrl } from '../utils/apiUtils';
import { ToastContext } from './ToastContext';
import {useTournamentAppConfiguration} from "../utils/tournamentconfig/TournamentConfiguration"

// Create the results context
export const ResultsContext = createContext();

export const ResultsProvider = ({ children }) => {
  const tournamentAppConfiguration = useTournamentAppConfiguration()

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false); // Keep this state for internal tracking
  const [error, setError] = useState(null);
  const [tournamentId, setTournamentId] = useState('');
  const [username, setUsername] = useState('');
  const [earlyType, setEarlyType] = useState(null);
  const [isTournamentPickerOpen, setIsTournamentPickerOpen] = useState(false);

  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const refreshIntervalRef = useRef(null);
  
  // Ref to store the current AbortController
  const abortControllerRef = useRef(null);
  // Ref to track the latest requested tournament ID
  const latestTournamentIdRef = useRef(null);

  // Get the toast context
  const { showToast } = useContext(ToastContext);

  /**
   * Update URL parameters without page reload
   * @param {Object} params - The parameters to update
   */
  const updateUrlParameters = useCallback((params) => {
    const url = new URL(window.location);
    
    // Update each parameter
    Object.keys(params).forEach(key => {
      if (params[key]) {
        url.searchParams.set(key, params[key]);
      } else {
        url.searchParams.delete(key);
      }
    });
    
    // Replace the current URL without reloading the page
    window.history.replaceState({}, '', url);
  }, []);

  // Wrap setUsername to also update URL and localStorage
  const updateUsername = useCallback((newUsername) => {
    setUsername(newUsername);
    localStorage.setItem('lastUsername', newUsername);
    updateUrlParameters({ username: newUsername });
  }, [updateUrlParameters]);

  // Wrap setTournamentIdOnly to also update URL and localStorage
  const updateTournamentId = useCallback((id, knownType = null) => {
    setTournamentId(id);
    localStorage.setItem('tournamentNumber', id);
    updateUrlParameters({ tournamentId: id });
    
    // If we know the tournament type, update the results with it
    if (knownType) {
      setEarlyType(knownType)
    }
  }, [updateUrlParameters]);

  // Fetch tournament results data
  const fetchResults = useCallback(async (id) => {
    if (!id) return;
    
    // Only show loading indicator if the tournament ID is changing
    const isTournamentIdChanging = id !== tournamentId;
    
    // Update the tournamentId state if a new ID is provided
    if (isTournamentIdChanging) {
      setTournamentId(id);
      setLoading(true); // Only show full loading when tournament ID changes
    } else {
      setRefreshing(true); // Keep track of refreshing state internally
      // Show toast notification for refresh
      showToast('Refreshing data...', 'info', 3000);
    }
    
    setError(null);

    // Abort any previous fetch request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create a new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    // Store the latest requested tournament ID
    latestTournamentIdRef.current = id;

    try {
      console.log(`Fetching results data for tournament: ${id}`);
      
      const response = await fetch(`${getBaseUrl()}${tournamentAppConfiguration.endPoints.results}${id}`, {
        credentials: 'include', // Important for sending cookies
        signal: abortController.signal // Add the abort signal to the fetch request
      });
      
      // Check if this is still the latest request
      if (latestTournamentIdRef.current !== id) {
        console.log(`Ignoring response for tournament ${id} as it's no longer the latest request`);
        return; // Exit early without updating state
      }
      
      if (!response.ok) {
        if (response.status === 500) {
          throw new Error('Server error: Please try selecting a different tournament or contact the developer for support.');
        }
        throw new Error(`Failed to load tournament data: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Double-check that this is still the latest request before updating state
      if (latestTournamentIdRef.current === id) {
        setResults(data);
        setLastFetchTime(new Date());
        console.log('Tournament data loaded successfully:', data);
        
        // Show success toast if refreshing (not initial load)
        if (!isTournamentIdChanging) {
          showToast('Data refreshed successfully', 'success', 3000);
        }
      } else {
        console.log(`Ignoring results for tournament ${id} as it's no longer the latest request`);
      }
    } catch (err) {
      // Ignore AbortError as it's expected when we cancel requests
      if (err.name === 'AbortError') {
        console.log(`Request for tournament ${id} was aborted`);
        return;
      }
      
      // Only update error state if this is still the latest request
      if (latestTournamentIdRef.current === id) {
        console.error('Error fetching results data:', err);
        setError(err.message || 'Failed to fetch results data. Please try again.');
        
        // Show error toast
        showToast(`Error: ${err.message || 'Failed to fetch data'}`, 'error', 5000);
      }
    } finally {
      // Only update loading/refreshing state if this is still the latest request
      if (latestTournamentIdRef.current === id) {
        if (isTournamentIdChanging) {
          setLoading(false); // Reset loading state if we set it to true
        }
        setRefreshing(false); // Always reset refreshing state
      }
    }
  }, [tournamentId, showToast, tournamentAppConfiguration.endPoints.results]);

  // Refresh data with the current tournamentId
  const refreshResults = useCallback(() => {
    if (tournamentId) {
      setRefreshCount(prevCount => prevCount + 1);
      // Use the current tournamentId for refresh (same ID, no loading indicator)
      fetchResults(tournamentId);
    }
  }, [tournamentId, fetchResults]);

  // Increment tournament ID
  const incrementTournamentId = useCallback(() => {
    if (tournamentId) {
      const nextId = (parseInt(tournamentId, 10) + 1).toString();
      updateTournamentId(nextId);
    }
  }, [tournamentId, updateTournamentId]);

  // Decrement tournament ID
  const decrementTournamentId = useCallback(() => {
    if (tournamentId) {
      const currentValue = parseInt(tournamentId, 10);
      if (currentValue > 1) {
        const prevId = (currentValue - 1).toString();
        updateTournamentId(prevId);
      }
    }
  }, [tournamentId, updateTournamentId]);

  // Auto-refresh functionality
  const startAutomaticRefresh = useCallback((minutesBetweenFetches = 5, maxRefreshes = 10) => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Reset refresh count
    setRefreshCount(0);
    
    // Convert minutes to milliseconds
    const milliseconds = minutesBetweenFetches * 60 * 1000;

    // Set up the interval
    const intervalId = setInterval(() => {
      setRefreshCount(prevCount => {
        if (prevCount < maxRefreshes) {
          console.log(`Auto-refreshing data (refresh ${prevCount + 1} of ${maxRefreshes})`);
          // Use the current tournamentId for auto-refresh
          if (tournamentId) {
            fetchResults(tournamentId);
          }
          return prevCount + 1;
        } else {
          console.log(`Reached maximum number of auto-refreshes (${maxRefreshes})`);
          clearInterval(intervalId);
          refreshIntervalRef.current = null;
          return prevCount;
        }
      });
    }, milliseconds);

    // Store the interval ID in the ref
    refreshIntervalRef.current = intervalId;

    return intervalId;
  }, [tournamentId, fetchResults]);

  // Fetch results when tournamentId changes
  useEffect(() => {
    if (tournamentId) {
      // This will show loading indicator since it's triggered by a tournamentId change
      fetchResults(tournamentId);
      startAutomaticRefresh(5); // 5 minutes
    } else {
      setResults(null);
    }
  }, [tournamentId, fetchResults, startAutomaticRefresh]);

  // Cleanup interval and abort any pending requests when component unmounts or tournamentId changes
  useEffect(() => {
    return () => {
      // Clear refresh interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
      
      // Abort any pending fetch requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [tournamentId]);

  // Load initial username from URL or localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlUsername = urlParams.get('username');
    const savedUsername = localStorage.getItem('lastUsername');
    
    if (urlUsername) {
      setUsername(urlUsername);
    } else if (savedUsername) {
      setUsername(savedUsername);
    }
  }, []);

  return (
    <ResultsContext.Provider 
      value={{ 
        results, 
        loading, 
        refreshing, // Keep this for backward compatibility
        error, 
        tournamentId,
        username,
        lastFetchTime,
        refreshCount,
        fetchResults,
        refreshResults,
        startAutomaticRefresh,
        setTournamentIdOnly: updateTournamentId,
        setUsername: updateUsername,
        earlyType,
        isTournamentPickerOpen,
        setIsTournamentPickerOpen,
        incrementTournamentId,
        decrementTournamentId
      }}
    >
      {children}
    </ResultsContext.Provider>
  );
};